import { PanelStatus } from "v2/components/StatusBadge";
import { IActivityHealth, ICnpjHealth, IWorkspaceHealth } from "./contracts/types";

/**
 * Regra de rolagem do spec §4.3 / plano B1: pior estado, nunca média.
 *
 * `AGUARDANDO` (T7) fica no fim, **abaixo de `SUCCESS`**, e isso não é
 * descuido: ele não julga, então nunca pode dominar uma rolagem. A posição é
 * obrigatória, não decorativa — `severityRank` é `indexOf`, que devolve `-1`
 * para quem está fora da lista, e `-1` ordena como o mais grave de todos.
 * Deixá-lo de fora faria o único estado desenhado para não acusar problema
 * vencer até `FAILED`. (O backend resolve o mesmo risco de outro jeito, por
 * filtro explícito em `rollupWorkspaceStatus`, porque lá o veredito é
 * calculado; aqui a rolagem é só de exibição.)
 */
export const SEVERITY_ORDER: PanelStatus[] = [
  "FAILED",
  "PENDING",
  "PAROU",
  "NUNCA_RODOU",
  "SEM_DADO",
  "SUCCESS",
  "AGUARDANDO",
];

export function severityRank(status: PanelStatus): number {
  return SEVERITY_ORDER.indexOf(status);
}

export function worstStatus(statuses: PanelStatus[]): PanelStatus {
  return statuses.reduce((worst, current) => (severityRank(current) < severityRank(worst) ? current : worst), "SUCCESS" as PanelStatus);
}

/** F0.1: "quem age, e quando" — só esta categoria entra no caminho de leitura por padrão. */
export function isActionableToday(status: PanelStatus): boolean {
  return status === "FAILED" || status === "PENDING" || status === "PAROU";
}

export function sortBySeverity<T>(items: T[], getStatus: (item: T) => PanelStatus): T[] {
  return [...items].sort((a, b) => severityRank(getStatus(a)) - severityRank(getStatus(b)));
}

export interface IFlatActivityEntry {
  cnpj: ICnpjHealth;
  workspace: IWorkspaceHealth;
  activityId: string;
  activityName: string;
  status: PanelStatus;
}

export function flattenActivities(cnpjs: ICnpjHealth[]): IFlatActivityEntry[] {
  const entries: IFlatActivityEntry[] = [];

  for (const cnpj of cnpjs) {
    for (const workspace of cnpj.workspaces) {
      for (const activity of workspace.activities) {
        entries.push({
          cnpj,
          workspace,
          activityId: activity.activityId,
          activityName: activity.activityName,
          status: activity.status,
        });
      }
    }
  }

  return entries;
}

// `ICauseAggregate`/`computeCauseAggregates` (F0.2, agregação por causa atrás
// de expandir/recolher) removidos no T9 (v2.1, 2026-08-19): eram exclusivos
// da `PortfolioPage.tsx` antiga. O backend (`getPanelHome`) agora faz essa
// classificação — `IPanelHomeCnpj.state` chega pronto, sem agregação no
// front. Confirmado por grep antes de remover: sem outro consumidor.

/**
 * Verdadeiro quando `cnpj` tem, em algum workspace, uma atividade que passa
 * em `statusFilter` e **não** está coberta por uma agregação por causa —
 * ou seja, um motivo pra aparecer sozinho na lista mesmo depois da
 * agregação (F0.2). Usado tanto pela visão padrão ("exige ação hoje")
 * quanto por "Ver todos": a diferença entre as duas é só qual
 * `statusFilter` cada uma passa, não a regra de cobertura em si.
 */
export function hasUncoveredProblem(
  cnpj: ICnpjHealth,
  aggregateActivityNames: Set<string>,
  statusFilter: (status: PanelStatus) => boolean
): boolean {
  return cnpj.workspaces.some((workspace) =>
    workspace.activities.some((activity) => statusFilter(activity.status) && !aggregateActivityNames.has(activity.activityName))
  );
}

/**
 * T10 (plano v2, 2026-08-11) — qual atividade a linha do CNPJ mostra.
 *
 * A versão anterior vivia em `PortfolioPage.tsx` e escolhia por
 * `activity.status === cnpj.status`, sem consultar `aggregateActivityNames`.
 * Isso desalinhava o render do critério que fez a linha existir: o CNPJ entra
 * na lista individual por causa de um problema **não coberto** por agregado
 * (`hasUncoveredProblem`), e então exibia o problema coberto — reproduzindo,
 * linha a linha, o que a linha agregada logo acima já tinha resumido. É o que
 * produzia "Carga na operadora (QBem) falhou em 9 de 12 workspaces" seguido de
 * cinco linhas repetindo "Carga na operadora (QBem)".
 *
 * Agora: a pior atividade **entre as não cobertas por agregado**, com fallback
 * para o comportamento antigo quando todas estão cobertas (caso da lista
 * expandida de um agregado, onde repetir a causa é justamente o ponto).
 */
export function pickLeadingActivity(
  cnpj: ICnpjHealth,
  aggregateActivityNames: Set<string> = new Set()
): IActivityHealth | undefined {
  const all = cnpj.workspaces.flatMap((workspace) => workspace.activities);
  const uncovered = all.filter((activity) => !aggregateActivityNames.has(activity.activityName));

  const pickWorst = (candidates: IActivityHealth[]): IActivityHealth | undefined =>
    candidates.reduce<IActivityHealth | undefined>(
      (worst, current) => (!worst || severityRank(current.status) < severityRank(worst.status) ? current : worst),
      undefined
    );

  return pickWorst(uncovered) ?? all.find((activity) => activity.status === cnpj.status) ?? all[0];
}

export interface IPortfolioCounters {
  saudaveis: number;
  comProblema: number;
}

/** Faixa de veredito (F0.3): contagem de CNPJs saudáveis vs. com problema hoje. */
export function computeCounters(cnpjs: ICnpjHealth[]): IPortfolioCounters {
  return {
    saudaveis: cnpjs.filter((cnpj) => cnpj.status === "SUCCESS").length,
    comProblema: cnpjs.filter((cnpj) => isActionableToday(cnpj.status)).length,
  };
}

/**
 * Contagem de atividades num dado estado.
 *
 * Já teve um parâmetro `isMuted` para descontar o que estivesse silenciado. O
 * silenciamento foi removido em 2026-08-11 (decisão do Gustavo): a
 * funcionalidade existia, foi testada, e era **inalcançável na prática** —
 * `MUTED_ACTIVITIES` nunca recebeu uma entrada desde o deploy. O botão exigia
 * que a atividade em destaque fosse `NUNCA_RODOU`/`SEM_DADO`, mas a única
 * lista da visão padrão que passava o handler era filtrada por
 * `isActionableToday` (`FAILED`/`PENDING`/`PAROU`) — as duas condições eram
 * mutuamente exclusivas por construção, então o botão nunca podia aparecer
 * ali.
 */
export function countVisibleByStatus(entries: IFlatActivityEntry[], status: PanelStatus): number {
  return entries.filter((entry) => entry.status === status).length;
}

/**
 * Sentinela para "nunca teve sucesso". É pior que qualquer número de dias, e
 * por isso não pode ser representado como número nenhum — nem `Infinity` (que
 * vaza para formatação e aritmética), nem um valor alto arbitrário.
 */
export const NEVER_SUCCEEDED = "NUNCA" as const;

export type WorstDelay = number | typeof NEVER_SUCCEEDED | undefined;

type DelayInput = Pick<IActivityHealth, "daysSinceLastSuccess" | "neverSucceeded" | "cadence">;

/**
 * T3 (plano v2, 2026-08-11) — o pior atraso entre as atividades dadas.
 *
 * Duas correções sobre a versão anterior, que era `Math.max` sobre
 * `.filter(v => v !== undefined)`:
 *
 * 1. **O pior caso era descartado.** `daysSinceLastSuccess` é ausente quando
 *    nunca houve sucesso — exatamente o que o `.filter` removia. O resultado
 *    era um sinal invertido: quanto mais quebrada a atividade, melhor o número
 *    da coluna. Agora "nunca" vence qualquer número, via `neverSucceeded`.
 * 2. **Ação de usuário não conta.** Uma atividade ON_DEMAND não tem defasagem
 *    por construção (o backend manda `daysSinceLastSuccess` ausente de
 *    propósito); incluí-la só podia sujar o agregado.
 *
 * `undefined` volta a significar uma coisa só: "não há nada aqui que meça
 * defasagem" — nunca "é o pior caso".
 */
export function worstDaysSinceLastSuccess(activities: DelayInput[]): WorstDelay {
  const judging = activities.filter((activity) => activity.cadence !== "ON_DEMAND");

  if (judging.some((activity) => activity.neverSucceeded)) return NEVER_SUCCEEDED;

  const values = judging.map((activity) => activity.daysSinceLastSuccess).filter((value): value is number => value !== undefined);
  if (values.length === 0) return undefined;
  return Math.max(...values);
}

/** Ordena atrasos para comparação/filtro: "nunca" é sempre o pior, ausente é sempre o menos grave. */
export function delayRank(delay: WorstDelay): number {
  if (delay === NEVER_SUCCEEDED) return Number.POSITIVE_INFINITY;
  return delay ?? -1;
}

/**
 * Ponto 2 do plano de melhorias de lógica (2026-08-10): "filtro de tempo" não
 * é janela de data (F0.2 já descartou isso), é limiar de atraso. Pior
 * `daysSinceLastSuccess` entre todas as atividades de todos os workspaces do
 * CNPJ — mesma função `worstDaysSinceLastSuccess` acima, só que agregada no
 * nível do CNPJ em vez de um workspace só.
 */
export function worstDaysSinceLastSuccessForCnpj(cnpj: ICnpjHealth): WorstDelay {
  return worstDaysSinceLastSuccess(cnpj.workspaces.flatMap((workspace) => workspace.activities));
}

/**
 * `resolveCnpjContractStatus` (rolagem workspace -> CNPJ do status de
 * contrato) saiu daqui: virou `mostVisibleContractStatus` +
 * `resolveContractStatusForCnpj` em `contractStatus.ts`, onde a tela inicial
 * também alcança. Este arquivo só tinha a versão que dependia da lista de
 * workspaces de `ICnpjHealth`, que o payload da tela inicial não tem.
 */
